import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import fileDownload from 'js-file-download';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DialogDefaultV2 } from '../../../../components/v2';
import { DropzoneDialogV2 } from '../../../../components/v2/DropzoneV2';
import FileService from '../../../../services/FileService';
import {
  apiErrorMessage,
  excluirKmzNsir,
  gerarKmzCompletoDaNsir,
  listarKmzNsir,
  listarKmzVisiveis,
} from '../../../../services/kmzMatriz';
import KmzArquivoMapDialog from './KmzArquivoMapDialog';
import {
  arquivosParaBaixar,
  itensParaVisualizar,
  legendaKmz,
  nomeDownloadKmz,
  normalizarListaKmz,
  resumoKmzCompleto,
  rotuloCurtoKmz,
  TIPO_COMPLETO,
  TIPO_INSPECAO,
} from './kmzAnexoAcoes';
import { camadasPadrao, getKmzCamadas, getKmzView, syncKmzNaUrl } from './kmzViewUrl';

const CONFIRMA = {
  remover: {
    title: 'Remover o KMZ',
    texto: 'O KMZ desta NSIR será apagado agora.',
    confirm: 'Remover',
  },
  completo: {
    title: 'Gerar o KMZ completo de novo',
    texto: 'O KMZ completo desta NSIR será substituído. Os serviços e as fotos entram de novo a partir da digitação atual.',
    confirm: 'Gerar de novo',
  },
};

function textoSemCoordenada(quantidade) {
  if (quantidade === 1) return '1 serviço ficou de fora, sem coordenada.';
  return `${quantidade} serviços ficaram de fora, sem coordenada.`;
}

const ResumoKmzCompleto = ({ data, nsmps }) => {
  const resumo = resumoKmzCompleto(data, nsmps);
  const legenda = resumo.incluidos === 1 ? 'serviço no KMZ' : 'serviços no KMZ';
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {resumo.incluidos}
          <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 0.75, fontWeight: 500 }}>
            {`de ${resumo.total}`}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {legenda}
        </Typography>
      </Box>
      {resumo.semCoordenada > 0 ? (
        <Box>
          <Typography variant="body2">
            {textoSemCoordenada(resumo.semCoordenada)}
          </Typography>
          {resumo.nomes.length ? (
            <Typography variant="body2" color="text.secondary">
              {resumo.nomes.join(', ')}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Box sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography variant="body2" color="text.secondary">
          Análise de risco e fotos iniciais não entram neste arquivo.
        </Typography>
      </Box>
    </Stack>
  );
};

ResumoKmzCompleto.propTypes = {
  data: PropTypes.object,
  nsmps: PropTypes.array,
};

ResumoKmzCompleto.defaultProps = {
  data: null,
  nsmps: [],
};

const KmzAnexoLinha = ({
  nsirId,
  arquivo,
  codigo,
  alimentadorCodigo,
  equipamento,
  alimentadorId,
  alimentadoresOptions,
  role,
  canEdit,
  canRemove,
  canGenerate,
  canGerarCompleto,
  nsmps,
  tipoServicoId,
  onSelectFile,
  onRemove,
  onGenerate,
}) => {
  const history = useHistory();
  const location = useLocation();
  const viewNaUrl = getKmzView(location.search);
  const camadasNaUrl = getKmzCamadas(location.search);
  const [uploadAberto, setUploadAberto] = useState(false);
  const [confirma, setConfirma] = useState(null);
  const [menuBaixar, setMenuBaixar] = useState(null);
  const [opcoesVer, setOpcoesVer] = useState([]);
  const [carregandoVer, setCarregandoVer] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [erroVer, setErroVer] = useState('');
  const [tituloErro, setTituloErro] = useState('Visualizar KMZ');
  const [itensRemotos, setItensRemotos] = useState([]);
  const [listaPronta, setListaPronta] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [baixandoResultado, setBaixandoResultado] = useState(false);
  const tentouUrl = useRef('');

  const paramsNome = {
    codigo,
    equipamento,
    alimentadorCodigo,
    alimentadorId,
    alimentadoresOptions,
  };
  const rotulo = rotuloCurtoKmz({ codigo, alimentadorCodigo });
  const temArquivo = arquivo != null;
  const completoRemoto = itensRemotos.find((entry) => entry.tipo === TIPO_COMPLETO) || null;
  const temCompleto = Boolean(completoRemoto);
  const baixaveis = arquivosParaBaixar({
    role,
    inspecao: arquivo,
    completo: completoRemoto ? { name: 'KMZ completo.kmz', url: completoRemoto.url } : null,
  });
  const mostraOlho = Boolean(nsirId) && temArquivo;
  const mostraGerarCompleto = Boolean(canGerarCompleto) && temArquivo && listaPronta;
  const rotuloGerarCompleto = temCompleto ? 'Gerar KMZ completo de novo' : 'Gerar KMZ completo';

  useEffect(() => {
    if (!nsirId) {
      setItensRemotos([]);
      setListaPronta(true);
      return undefined;
    }
    let ativo = true;
    setListaPronta(false);
    listarKmzVisiveis(nsirId)
      .then((data) => {
        if (ativo) setItensRemotos(normalizarListaKmz(data));
      })
      .catch(() => {
        if (ativo) setItensRemotos([]);
      })
      .finally(() => {
        if (ativo) setListaPronta(true);
      });
    return () => {
      ativo = false;
    };
  }, [nsirId]);

  const baixar = async (item) => {
    setMenuBaixar(null);
    if (!item) return;
    try {
      if (item.url && nsirId) {
        const consulta = await listarKmzNsir(nsirId, item.tipo);
        const fresco = normalizarListaKmz(consulta && consulta.data).find((entry) => entry.tipo === item.tipo);
        if (!fresco || !fresco.url) throw new Error('O arquivo não está disponível.');
        const response = await FileService.getFile(fresco.url);
        const nome = nomeDownloadKmz(paramsNome, { name: 'arquivo.kmz' }, item.tipo);
        fileDownload(response.data, nome);
        return;
      }
      if (item.file) fileDownload(item.file, nomeDownloadKmz(paramsNome, item.file, item.tipo));
    } catch (error) {
      setTituloErro('Baixar KMZ');
      setErroVer(apiErrorMessage(error, 'Não foi possível baixar o KMZ.'));
    }
  };

  const aoBaixarGerado = async () => {
    const item = itensRemotos.find((entry) => entry.tipo === TIPO_COMPLETO);
    if (!item) {
      setTituloErro('Baixar KMZ');
      setErroVer('O arquivo não está disponível.');
      return;
    }
    setBaixandoResultado(true);
    try {
      await baixar(item);
    } finally {
      setBaixandoResultado(false);
    }
  };

  const aoBaixar = (event) => {
    if (baixaveis.length > 1) {
      setMenuBaixar(event.currentTarget);
      return;
    }
    baixar(baixaveis[0]);
  };

  const aoVisualizar = async (forcarTipo) => {
    if (!nsirId) return;
    tentouUrl.current = String(nsirId);
    setCarregandoVer(true);
    setTituloErro('Visualizar KMZ');
    setErroVer('');
    try {
      const data = await listarKmzVisiveis(nsirId);
      const remotos = normalizarListaKmz(data);
      setItensRemotos(remotos);
      const itens = remotos.some((entry) => entry.tipo === TIPO_INSPECAO) || !arquivo
        ? remotos
        : [
          ...remotos,
          { tipo: TIPO_INSPECAO, url: '', label: 'KMZ para inspeção' },
        ];
      const visiveis = itensParaVisualizar(role, itens);
      if (!visiveis.length) {
        setErroVer('Não há KMZ para visualizar.');
        return;
      }
      const daUrl = getKmzCamadas(location.search);
      const pedidas = forcarTipo ? [forcarTipo] : daUrl;
      const marcadas = (pedidas || [])
        .filter((tipo) => visiveis.some((entry) => entry.tipo === tipo));
      syncKmzNaUrl(history, location, {
        aberto: true,
        camadas: marcadas.length ? marcadas : camadasPadrao(visiveis),
      });
      setOpcoesVer(visiveis);
    } catch (error) {
      setErroVer(apiErrorMessage(error, 'Não foi possível listar os KMZ.'));
    } finally {
      setCarregandoVer(false);
    }
  };

  useEffect(() => {
    if (!viewNaUrl) {
      tentouUrl.current = '';
      return;
    }
    const chave = String(nsirId || '');
    if (tentouUrl.current === chave || opcoesVer.length > 0 || carregandoVer || !nsirId || !temArquivo) return;
    tentouUrl.current = chave;
    aoVisualizar();
  }, [viewNaUrl, nsirId, temArquivo, opcoesVer.length, carregandoVer]);

  const aoGerarCompleto = async () => {
    if (!nsirId || gerando) return;
    setGerando(true);
    setTituloErro('Gerar KMZ completo');
    setErroVer('');
    try {
      const response = await gerarKmzCompletoDaNsir(nsirId);
      const data = await listarKmzVisiveis(nsirId);
      setItensRemotos(normalizarListaKmz(data));
      setResultado(response && response.data ? response.data : {});
    } catch (error) {
      setErroVer(apiErrorMessage(error, 'Não foi possível gerar o KMZ completo.'));
    } finally {
      setGerando(false);
    }
  };

  const fecharConfirma = async (seguir) => {
    const acao = confirma;
    if (!seguir || !acao || removendo) {
      if (!removendo) setConfirma(null);
      return;
    }
    if (acao === 'completo') {
      setConfirma(null);
      aoGerarCompleto();
      return;
    }
    if (!nsirId) {
      setConfirma(null);
      onRemove();
      return;
    }
    setRemovendo(true);
    try {
      await excluirKmzNsir(nsirId);
      setConfirma(null);
      setItensRemotos([]);
      onRemove();
    } catch (error) {
      setConfirma(null);
      setTituloErro('Remover o KMZ');
      setErroVer(apiErrorMessage(error, 'Não foi possível remover o KMZ.'));
    } finally {
      setRemovendo(false);
    }
  };

  const dialogo = confirma ? CONFIRMA[confirma] : null;
  const nomeGuardado = (arquivo && arquivo.name) || rotulo;

  return (
    <Box>
      {!temArquivo ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {canGenerate ? (
            <Button
              variant="outlined"
              onClick={onGenerate}
            >
              Gerar trecho a partir da matriz
            </Button>
          ) : null}
          <Button
            variant="contained"
            disabled={!canEdit}
            onClick={() => setUploadAberto(true)}
          >
            Adicionar Arquivo KMZ
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
          <InsertDriveFileOutlinedIcon
            fontSize="small"
            sx={{ color: 'primary.main', flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0, mr: 0.5 }}>
            <Tooltip title={nomeGuardado}>
              <Typography
                variant="body2"
                noWrap
                sx={{ maxWidth: 280 }}
              >
                {rotulo}
              </Typography>
            </Tooltip>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              {legendaKmz({ inspecao: arquivo, completo: temCompleto ? completoRemoto : null })}
            </Typography>
          </Box>
          {mostraOlho ? (
            <Tooltip title="Visualizar">
              <span>
                <IconButton
                  size="small"
                  disabled={carregandoVer}
                  onClick={() => aoVisualizar()}
                >
                  {carregandoVer ? (
                    <CircularProgress size={16} />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {baixaveis.length > 0 ? (
            <Tooltip title="Baixar">
              <IconButton
                size="small"
                onClick={aoBaixar}
              >
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {mostraGerarCompleto ? (
            <Tooltip title={rotuloGerarCompleto}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={gerando}
                  aria-label={rotuloGerarCompleto}
                  onClick={() => (temCompleto ? setConfirma('completo') : aoGerarCompleto())}
                >
                  {gerando ? (
                    <CircularProgress size={16} />
                  ) : (
                    <LayersOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {canRemove ? (
            <Tooltip title="Remover">
              <IconButton
                size="small"
                disabled={removendo}
                aria-label="Remover"
                onClick={() => setConfirma('remover')}
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      )}

      <Menu
        anchorEl={menuBaixar}
        open={Boolean(menuBaixar)}
        onClose={() => setMenuBaixar(null)}
      >
        {baixaveis.map((item) => (
          <MenuItem
            key={item.tipo}
            onClick={() => baixar(item)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <DialogDefaultV2
        open={Boolean(erroVer)}
        readOnly
        handlerCancel={() => setErroVer('')}
        handlerConfirm={() => {}}
        title={tituloErro}
      >
        <Typography variant="body2">{erroVer}</Typography>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={Boolean(dialogo)}
        handlerCancel={() => fecharConfirma(false)}
        handlerConfirm={() => fecharConfirma(true)}
        title={dialogo ? dialogo.title : ''}
        confirmMessage={dialogo ? dialogo.confirm : 'Continuar'}
        confirmLoading={removendo}
        confirmLoadingMessage="Removendo..."
        cancelMessage="Cancelar"
      >
        <Typography variant="body2">{dialogo ? dialogo.texto : ''}</Typography>
      </DialogDefaultV2>

      <DropzoneDialogV2
        open={uploadAberto}
        onClose={() => setUploadAberto(false)}
        onSave={(files) => {
          setUploadAberto(false);
          if (files && files.length > 0) onSelectFile(files[0]);
        }}
        acceptedFiles={['.kmz', '.pdf', '.xls', '.xlsb']}
        filesLimit={1}
        dialogTitle="Selecionar arquivo KMZ"
      />

      <DialogDefaultV2
        open={Boolean(resultado)}
        handlerCancel={() => setResultado(null)}
        handlerConfirm={() => {
          setResultado(null);
          aoVisualizar(TIPO_COMPLETO);
        }}
        title="KMZ completo"
        confirmMessage="Ver no mapa"
        cancelMessage="Fechar"
        extraActions={[
          {
            label: baixandoResultado ? 'Baixando...' : 'Download',
            variant: 'outlined',
            disabled: baixandoResultado,
            onClick: aoBaixarGerado,
          },
        ]}
      >
        {resultado ? <ResumoKmzCompleto data={resultado} nsmps={nsmps} /> : null}
      </DialogDefaultV2>

      <KmzArquivoMapDialog
        open={viewNaUrl && opcoesVer.length > 0}
        onClose={() => syncKmzNaUrl(history, location, { aberto: false })}
        itens={opcoesVer}
        camadasMarcadas={camadasNaUrl}
        onCamadasChange={(camadas) => syncKmzNaUrl(history, location, { aberto: true, camadas })}
        arquivoInspecao={arquivo}
        nsirId={nsirId}
        tipoServicoId={tipoServicoId}
        nomeBase={nomeDownloadKmz(paramsNome, { name: 'arquivo.kmz' }).replace(/\.kmz$/i, '')}
      />
    </Box>
  );
};

KmzAnexoLinha.propTypes = {
  nsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  arquivo: PropTypes.object,
  codigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  alimentadorCodigo: PropTypes.string,
  equipamento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  alimentadorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  alimentadoresOptions: PropTypes.array,
  role: PropTypes.string,
  canEdit: PropTypes.bool,
  canRemove: PropTypes.bool,
  canGenerate: PropTypes.bool,
  canGerarCompleto: PropTypes.bool,
  nsmps: PropTypes.array,
  tipoServicoId: PropTypes.string,
  onSelectFile: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};

KmzAnexoLinha.defaultProps = {
  nsirId: null,
  arquivo: null,
  codigo: '',
  alimentadorCodigo: '',
  equipamento: '',
  alimentadorId: null,
  alimentadoresOptions: [],
  role: '',
  canEdit: false,
  canRemove: false,
  canGenerate: false,
  canGerarCompleto: false,
  nsmps: [],
  tipoServicoId: '',
};

export default KmzAnexoLinha;
